/* eslint-disable no-underscore-dangle */
import { isEmpty } from 'lodash-es';

import requester from '../../../js/utils/requester';

import BaseArchiveModel from './page-archive--model';

export default class PageArchiveFormation extends BaseArchiveModel {
	initialize() {
		super.initialize();

		this.targetsItems = this.getFilterItems( 'targets' );
		this.categoriesItems = this.getFilterItems( 'categories' );
		this.statusesItems = this.getFilterItems( 'statuses' );
		this.locationsItems = this.getFilterItems( 'locations' );
		this.yearsItems = this.getFilterItems( 'years', ( o ) => o.id );
	}

	getInitialState() {
		const queryArgs = new URLSearchParams( window.location.search );

		return {
			...super.getInitialState(),
			search: queryArgs.get( 'q' ) ?? '',
		};
	}

	getAggs() {
		return {
			targets: {
				nested: { path: 'targets' },
				aggs: {
					unique: {
						terms: { field: 'targets.slug', size: 1000, order: { _key: 'asc' } },
						aggs: { title: { terms: { field: 'targets.title', size: 1 } } },
					},
				},
			},
			categories: {
				aggs: {
					unique: {
						terms: {
							field: 'category.title.keyword',
							size: 1000,
							min_doc_count: 1,
							order: { _key: 'asc' },
						},
					},
				},
			},
			statuses: {
				aggs: {
					unique: {
						terms: {
							field: 'status',
							size: 1000,
							min_doc_count: 1,
							order: { _key: 'asc' },
						},
					},
				},
			},
			locations: {
				aggs: {
					unique: {
						terms: {
							field: 'location',
							size: 1000,
							min_doc_count: 1,
							order: { _key: 'asc' },
						},
					},
				},
			},
			dates: {
				global: {},
				aggs: {
					unique: {
						date_histogram: {
							field: 'date',
							calendar_interval: 'year',
							format: 'yyyy-MM-dd',
							order: { _key: 'desc' },
						},
					},
				},
			},
		};
	}

	getQuery() {
		// Empty
		const queryArgs = [];

		// search text
		if ( ! isEmpty( this.state.search ) ) {
			const sanitizedSearch = this.sanitizeSearch( this.state.search );

			queryArgs.push( {
				bool: {
					should: [
						{ match: { title: { query: sanitizedSearch } } },
						{ match: { 'category.title': { query: sanitizedSearch } } },
					],
				},
			} );
		}

		// Filters selected
		if ( ! isEmpty( this.state.filters.targets ) ) {
			queryArgs.push( {
				nested: {
					path: 'targets',
					query: {
						bool: {
							must: [
								{
									terms: {
										'targets.title': this.state.filters.targets.map( ( v ) => this.targetsItems[ v ] ),
									},
								},
							],
						},
					},
				},
			} );
		}
		if ( ! isEmpty( this.state.filters.categories ) ) {
			queryArgs.push( {
				terms: {
					'category.title.keyword': this.state.filters.categories.map( ( v ) => this.categoriesItems[ v ] ),
				},
			} );
		}
		if ( ! isEmpty( this.state.filters.statuses ) ) {
			queryArgs.push( {
				terms: {
					status: this.state.filters.statuses.map( ( v ) => this.statusesItems[ v ] ),
				},
			} );
		}
		else if ( this.statusesItems?.archives ) {
			// Default behavior: archived items are excluded unless explicitly selected.
			queryArgs.push( {
				bool: {
					must_not: [
						{
							term: {
								status: this.statusesItems.archives,
							},
						},
					],
				},
			} );
		}
		if ( ! isEmpty( this.state.filters.locations ) ) {
			queryArgs.push( {
				terms: {
					location: this.state.filters.locations.map( ( v ) => this.locationsItems[ v ] ),
				},
			} );
		}
		if ( ! isEmpty( this.state.filters.years ) ) {
			queryArgs.push( {
				bool: {
					should: this.state.filters.years.map( ( v ) => {
						const yearDate = this.yearsItems[ v ];
						return {
							range: {
								date: {
									gte: yearDate,
									lt: `${ yearDate }||+12M`,
								},
							},
						};
					} ),
				},
			} );
		}

		return {
			bool: {
				must: queryArgs,
			},
		};
	}

	getSort() {
		const timeInMs = Date.now();
		const statusOrder = [
			'inscriptions ouvertes',
			'inscriptions a venir',
			'inscriptions closes',
			'archives',
		];

		const sort = [
			{
				_script: {
					type: 'number',
					order: 'asc',
					script: {
						lang: 'painless',
						params: { statusOrder },
						source: 'if (doc["status"].size() == 0) return 999; String s = doc["status"].value.toLowerCase(); s = s.replace("à", "a"); int idx = params.statusOrder.indexOf(s); return idx >= 0 ? idx : 999;',
					},
				},
			},
			{
				_script: {
					type: 'number',
					order: 'asc',

					script: {
						lang: 'painless',
						params: { now: timeInMs },
						source: 'if (doc["date"].size() == 0) return 2;long d = doc["date"].value.toInstant().toEpochMilli();return d >= params.now ? 0: 1;',
					},
				},
			},
			{
				_script: {
					type: 'number',
					order: 'asc',

					script: {
						lang: 'painless',
						params: { now: timeInMs },
						source: 'if (doc["date"].size() == 0) return Long.MAX_VALUE;long d = doc["date"].value.toInstant().toEpochMilli();return d >= params.now ? d: -d;',
					},
				},
			},
			{ id: 'asc' },
		];

		if ( ! isEmpty( this.state.search ) ) {
			sort.unshift( '_score' );
		}

		return sort;
	}

	async prepareHits( hits ) {
		const args = {
			name: 'pageArchive:query',
			url: `/wp-json/hesav/v1/formation/cards-context`,
			method: 'post',
			data: { items: hits.map( ( hit ) => hit._source ) },
		};

		const { data } = await requester( args );

		return data;
	}

	getCardContext( context ) {
		return context;
	}

	renderIcon( icon ) {
		switch ( icon ) {
			case 'partner':
				return '<svg width="32" height="32" viewbox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10 15.333c-1.283 0-2.382-.457-3.296-1.37-.914-.914-1.37-2.013-1.37-3.296 0-1.284.456-2.382 1.37-3.296C7.618 6.457 8.717 6 10 6s2.382.457 3.296 1.371c.914.914 1.37 2.012 1.37 3.296 0 1.283-.456 2.382-1.37 3.296-.914.913-2.013 1.37-3.296 1.37ZM0 25.487v-2.964c0-.653.177-1.258.532-1.814a3.55 3.55 0 0 1 1.422-1.283 19.345 19.345 0 0 1 3.988-1.454 17.266 17.266 0 0 1 8.116 0c1.34.323 2.67.808 3.988 1.454.593.299 1.067.727 1.422 1.283A3.31 3.31 0 0 1 20 22.523v2.964H0Zm2-2h16v-.964c0-.27-.078-.52-.235-.75a1.778 1.778 0 0 0-.637-.563 17.156 17.156 0 0 0-3.514-1.286 15.196 15.196 0 0 0-7.228 0c-1.194.292-2.365.72-3.514 1.286-.269.146-.48.333-.637.563-.157.23-.235.48-.235.75v.964Zm8-10.154c.733 0 1.361-.26 1.883-.783.523-.522.784-1.15.784-1.883 0-.734-.261-1.361-.784-1.884A2.568 2.568 0 0 0 10 8c-.733 0-1.361.261-1.883.783a2.568 2.568 0 0 0-.784 1.884c0 .733.261 1.36.784 1.883.522.522 1.15.783 1.883.783ZM22 15.333c-1.283 0-2.382-.457-3.296-1.37-.914-.914-1.37-2.013-1.37-3.296 0-1.284.456-2.382 1.37-3.296C19.618 6.457 20.717 6 22 6s2.382.457 3.296 1.371c.914.914 1.37 2.012 1.37 3.296 0 1.283-.456 2.382-1.37 3.296-.914.913-2.013 1.37-3.296 1.37Zm0-2c.733 0 1.361-.26 1.883-.783.523-.522.784-1.15.784-1.883 0-.734-.262-1.361-.784-1.884A2.568 2.568 0 0 0 22 8c-.733 0-1.361.261-1.883.783a2.568 2.568 0 0 0-.784 1.884c0 .733.261 1.36.784 1.883.522.522 1.15.783 1.883.783Z" fill="currentColor"/><path d="M22 17.487c-1.364 0-2.717.162-4.058.485-.837.202-2.497.793-2.497.793s1.396.985 2.94 1.16a15.197 15.197 0 0 1 7.23 0 17.15 17.15 0 0 1 3.513 1.285c.268.146.48.333.637.563.157.23.235.48.235.75v.964H14v2h18v-2.964a3.31 3.31 0 0 0-.532-1.814 3.55 3.55 0 0 0-1.422-1.283 19.346 19.346 0 0 0-3.988-1.454A17.265 17.265 0 0 0 22 17.487Z" fill="currentColor"/></svg>';
			case 'formation-32px':
				return '<svg width="32" height="32" viewbox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16 27.2L7.2 22.8V14.8L1.6 12L16 4.8L30.4 12V22.4H28V13.2L24.8 14.8V22.8L16 27.2ZM16 16.5L25.033 12L16 7.5L6.967 12L16 16.5ZM16 24.5L22.4 21.3V16L16 19.2L9.6 16V21.3L16 24.5Z" fill="currentColor"/></svg>';
			case 'event':
				return '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M21.75 17.25V4.5H2.25V21.75H17.25M21.75 17.25L17.25 21.75M21.75 17.25H17.25V21.75M2.25 9.75H21.75M7.5 2.25V6.75M16.5 2.25V6.75" stroke="currentColor" stroke-width="1.5"/></svg>';
			case 'location':
				return '<svg width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.002 11.865c.497 0 .922-.177 1.276-.531.353-.355.53-.78.53-1.278 0-.497-.178-.923-.532-1.276a1.746 1.746 0 0 0-1.278-.53c-.497 0-.923.177-1.276.531-.353.355-.53.78-.53 1.278s.177.923.532 1.277c.354.353.78.53 1.278.53ZM12 19.514c1.956-1.752 3.453-3.432 4.491-5.04 1.038-1.608 1.557-3.017 1.557-4.226 0-1.823-.58-3.322-1.738-4.496-1.158-1.174-2.595-1.761-4.31-1.761-1.715 0-3.152.587-4.31 1.761-1.159 1.174-1.738 2.673-1.738 4.496 0 1.209.519 2.618 1.557 4.226 1.037 1.608 2.535 3.288 4.49 5.04Zm0 1.995c-2.517-2.18-4.404-4.21-5.662-6.088-1.257-1.878-1.886-3.602-1.886-5.173 0-2.308.746-4.176 2.24-5.605C8.183 3.214 9.953 2.5 12 2.5s3.816.714 5.309 2.143c1.492 1.43 2.239 3.297 2.239 5.605 0 1.57-.629 3.295-1.887 5.173C16.404 17.3 14.517 19.33 12 21.51Z" fill="currentColor"/></svg>';
			case 'info':
				return '<svg width="24" height="24" viewbox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.002 21.5a9.254 9.254 0 0 1-3.706-.748 9.596 9.596 0 0 1-3.016-2.03 9.595 9.595 0 0 1-2.032-3.016 9.246 9.246 0 0 1-.748-3.704c0-1.314.25-2.55.748-3.706a9.596 9.596 0 0 1 2.03-3.016 9.594 9.594 0 0 1 3.016-2.032 9.246 9.246 0 0 1 3.704-.748c1.314 0 2.55.25 3.706.748a9.596 9.596 0 0 1 3.017 2.03 9.594 9.594 0 0 1 2.03 3.016 9.247 9.247 0 0 1 .749 3.704c0 1.314-.25 2.55-.748 3.706a9.596 9.596 0 0 1-2.03 3.017 9.595 9.595 0 0 1-3.016 2.03 9.247 9.247 0 0 1-3.704.749ZM12 20c2.217 0 4.104-.78 5.663-2.337C19.22 16.104 20 14.217 20 12s-.78-4.104-2.337-5.662C16.104 4.779 14.217 4 12 4s-4.104.78-5.662 2.338C4.779 7.896 4 9.783 4 12s.78 4.104 2.338 5.663C7.896 19.22 9.783 20 12 20Z" fill="currentColor"/><path d="M11.284 17V9.944h1.778V17h-1.778Zm-.056-8.246V6.99h1.862v1.764h-1.862Z" fill="currentColor"/></svg>';
			default:
				return '';
		}
	}

	renderCard( { modifiers = [], data = {} } ) {
		return `
		<article class="card-formation ${ modifiers.join( ' ' ) }">
			<div class="card-formation__inner">

				<div class="card-formation__header">
					${ data.header.isNew ? '<span class="card-formation__header_new">Nouveau</span>' : '' }
					${ data.header.tag ? `<span class="tag"><span class="tag__text">${ data.header.tag }</span></span>` : '' }
					<div class="card-formation__image">
						${ data?.header?.image?.picture ? `<picture>${ data.header?.image?.picture }</picture>` : '' }
					</div>
					<span class="card-formation__header_icon">
						${ this.renderIcon( 'formation-32px' ) }
					</span>
				</div>

				<div class="card-formation__content">
					${ data.uptitle ? `<p class="card-formation__uptitle">${ data.uptitle }</p>` : '' }
					<p class="card-formation__title">${ data.title }</p>
					${ data.subtitle ? `<p class="card-formation__subtitle">${ data.subtitle }</p>` : '' }

					<ul class="card-formation__infos">
						${ data.infos.map( ( info ) => `<li>
							${ info?.icon ? `<span class="card-formation__info-icon">${ this.renderIcon( info.icon ) }</span>` : '' }
							<span class="card-formation__info-label">${ info?.label ?? '&nbsp;' }</span>
						</li>` ).join( '' ) }
					</ul>

					<div class="card-formation__buttons">
						${ data.buttons.map( ( btn ) => `
							<div class="wp-block-button ${ btn.modifiers.join( ' ' ) }">
								<a class="wp-block-button__link" href="${ btn.data.link }">
									${ btn.data.title }
								</a>
							</div>
						` ).join( '' ) }
					</div>
				</div>

			</div>
		</article>`;
	}
}
